import { useRef, useEffect, useState, useCallback, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { updateModelInfos, updateModelGroups } from "../../redux/casesRedux";
import { useParams } from "react-router-dom";
import * as THREE from "three";
import { TrackballControls } from "three/examples/jsm/controls/TrackballControls";
import { STLLoader } from "three/examples/jsm/loaders/STLLoader";
import { Tween, Easing, Group } from "@tweenjs/tween.js";
import ModelsSidebar from "./sidebar/ModelsSidebar";
import AnnotationsSidebar from "./sidebar/AnnotationsSidebar";
import MeasurementsSidebar from "./sidebar/MeasurementsSidebar";
import AnnotationModal from "./modals/AnnotationModal";
import MeasurementModal from "./modals/MeasurementModal";
import AddSurgeryPosModal from "./modals/AddSurgeryPosModal";
import SurgeryPositionModal from "./modals/SurgeryPositionModal";
import SectionViewModal from "./modals/SectionViewModal";
import Tabs from "./sidebar/Tabs";
import useIsMobile from "../../hooks/useIsMobile";
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import { ref, update } from "firebase/database";
import { database } from "../../firebase";
import useDrillMode from "../../hooks/useDrillMode";
import useScalpelMode from "../../hooks/useScalpelMode";
import DrillModal from "./modals/DrillModal";
import DistanceMeasuringModal from "./modals/DistanceMeasuringModal";
import CameraOrientationModal from "./modals/CameraOrientationModal";
import ScalpelModal from "./modals/ScalpelModal";
import useFullScreen from "../../hooks/useFullScreen";
import useCamera from "../../hooks/useCamera";
import Pins from "./Pins";
import useMeasurement from "../../hooks/useMeasurement";
import useAnnotations from "../../hooks/useAnnotations";
import useSurgeryPos from "../../hooks/useSurgeryPos";
import useSectionView from "../../hooks/useSectionView";
import SideButtonsControls from "./SideButtonsControls";
import VideoSnackbar from "./VideoSnackbar";
import { useDicomStore } from "../../redux/dicomRedux";
import useSectionViewSimple from "../../hooks/useSectionViewSimple";
import SectionViewModalSimple from "./modals/SectionViewModalSimple";
import OnScreenNav from "./OnscreenNav";
import { ViewportGizmo } from "three-viewport-gizmo";
import usePointLinking from "../../hooks/usePointLinking";
import useExtractMode from "../../hooks/useExtractMode";
import useStentMode from "../../hooks/useStentMode";
import StentModal from "./modals/StentModal";
import ExtractModal from "./modals/ExtractModal";
import CameraSliderModal from "./modals/CameraSliderModal";
import SectionViewSliderModal from "./modals/SectionViewSliderModal";
import CrossSectionModal from "./modals/CrossSectionModal";
import { useCenterline } from "../../hooks/useCenterline";
import CenterlineModal from "./modals/CenterlineModal";
import CenterlineMeshSelectionModal from "./modals/CenterlineMeshSelectionModal";
import useTargetMesh from "../../hooks/useTargetMesh";
import ModelGroupingModal from "../modals/ModelGroupingModal";
import useCaseDetail from "../../hooks/useCaseDetail";

const LAYER_OPAQUE = 0; // default pass
const LAYER_TRANSPARENT = 1; // second pass for any mesh with opacity < 1
const TRANSPARENT_BIAS = 10000; // big enough to be "after everything opaque"

function makeResourceTracker() {
  const resources = new Set();

  const track = (res) => {
    if (!res) return res;
    if (Array.isArray(res)) {
      res.forEach(track);
      return res;
    }
    if (
      res.dispose ||
      res.isObject3D ||
      res.isTexture ||
      res.isMaterial ||
      res.isBufferGeometry ||
      res.isRenderTarget
    ) {
      resources.add(res);
    }
    if (res.isObject3D) {
      res.traverse((obj) => {
        resources.add(obj);
        if (obj.geometry) resources.add(obj.geometry);
        if (obj.material) {
          if (Array.isArray(obj.material))
            obj.material.forEach((m) => resources.add(m));
          else resources.add(obj.material);
        }
      });
    }
    return res;
  };

  const disposeMaterial = (mat) => {
    if (!mat) return;
    const mats = Array.isArray(mat) ? mat : [mat];
    mats.forEach((m) => {
      Object.keys(m).forEach((k) => {
        const v = m[k];
        if (v?.isTexture) v.dispose();
        if (v?.isRenderTarget) v.dispose();
      });
      m.dispose?.();
    });
  };

  const dispose = () => {
    resources.forEach((r) => {
      if (!r) return;
      if (r.isMesh || r.isLine || r.isPoints) {
        r.geometry?.dispose?.();
        r.material && disposeMaterial(r.material);
      } else if (r.isMaterial) {
        disposeMaterial(r);
      } else if (r.isBufferGeometry || r.isTexture || r.isRenderTarget) {
        r.dispose?.();
      } else if (r.dispose) {
        r.dispose();
      }
      if (r.isObject3D && r.parent) r.parent.remove(r);
    });
    resources.clear();
  };

  return { track, dispose };
}

const ModelViewer = ({ stlURLs, annotations, measurements, isFullScreen, modelGroups,
}) => {
  const thisCase = useSelector((state) => state.cases.selectedCase);
  const selectedCaseDetail = useSelector(
    (state) => state.cases.selectedCaseDetail
  );
  const user = useSelector((state) => state.user);
  const dispatch = useDispatch();
  // TODO if this params take the argument from the url. This needs to be revisited since changing the a little bit url still shows the same model.
  const { id } = useParams();
  const rootWrapperRef = useRef(null);
  const containerRef = useRef(null);
  const modelRef = useRef(new THREE.Object3D());
  const controlsRef = useRef(null);
  const cameraRef = useRef(null);
  const sceneRef = useRef(null);
  const undoStack = useRef([]);
  const undoIndex = useRef(0);
  const undoMeasurementStack = useRef([]);
  const rendererRef = useRef(null);
  const rafIdRef = useRef(0);
  const gizmoRef = useRef(null);
  const viewportRef = useRef({ left: 0, top: 0, width: 0, height: 0 });
  const resizeHandlerRef = useRef(null);
  const hotkeyHandlersRef = useRef(null);
  const myTweenGroup = useRef(new Group()).current;
  const [activeTab, setActiveTab] = useState("models");
  const [numOfLoadedModels, setNumOfLoadedModels] = useState(0);
  const [isModelLoading, setIsModelLoading] = useState(true);
  const [initialCameraPosition, setInitialCameraPosition] = useState(null);
  const [initialCameraTarget, setInitialCameraTarget] = useState(null);
  const [selectedModels, setSelectedModels] = useState([]);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const isMobile = useIsMobile();
  const [processing, setProcessing] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [videoStatus, setVideoStatus] = useState(null);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const videoRecorderRef = useRef(null);
  const videoChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);
  const dicomBounds = useDicomStore((s) => s.dicomBounds);
  const [isLandscape, setIsLandscape] = useState(
    typeof window !== "undefined"
      ? window.innerWidth > window.innerHeight
      : false
  );
  const [isCameraSliderModalOpen, setIsCameraSliderModalOpen] = useState(false);
  const [isSectionViewSliderModalOpen, setIsSectionViewSliderModalOpen] =
    useState(false);
  const [showGroupingModal, setShowGroupingModal] = useState(false);

  const { handleSaveModelGroups } = useCaseDetail(
    () => { },
    () => { },
    () => { },
    () => { }
  );

  const { isTargetMeshMode, toggleTargetMeshMode, targetedIndices } =
    useTargetMesh({
      containerRef,
      cameraRef,
      modelRef,
      viewportRef,
      modelGroups: (() => {
        const modelGroups = selectedCaseDetail?.modelGroups || {};
        const userId = user?.currentUser?.id;
        if (userId && modelGroups[userId]) {
          return modelGroups[userId];
        }
        return modelGroups["common"] || [];
      })(),
    });

  const handleOpacityChange = (index, opacity) => {
    const mesh = modelRef.current?.children[index];
    if (!mesh) return;

    const isTransparent = opacity < 1;
    const m = mesh.material;
    m.opacity = opacity;
    m.transparent = isTransparent;
    m.depthTest = true;
    m.depthWrite = !isTransparent;
    mesh.castShadow = !isTransparent;
    mesh.receiveShadow = !isTransparent;
    //mesh.material.side = isTransparent ? THREE.FrontSide : THREE.DoubleSide;
    m.needsUpdate = true;

    //setTransparencyLayersRecursive(mesh, isTransparent);

    if (mesh.userData.baseRenderOrder == null) {
      mesh.userData.baseRenderOrder = mesh.renderOrder || 0;
    }

    if (isTransparent) {
      setRenderOrderRecursive(
        mesh,
        mesh.userData.baseRenderOrder + TRANSPARENT_BIAS
      );
    } else {
      setRenderOrderRecursive(mesh, mesh.userData.baseRenderOrder);
    }
  };

  const handleVisibilityChange = (index, value) => {
    if (
      sceneRef.current &&
      modelRef.current &&
      modelRef.current.children[index]
    ) {
      modelRef.current.children[index].visible = value;
    }
  };

  const {
    isDrillMode,
    setIsDrillMode,
    brushSize,
    setBrushSize,
    drillShape,
    setDrillShape,
    undoDrill,
    performDrillAuto,
    redoDrill,
  } = useDrillMode(
    sceneRef,
    cameraRef,
    modelRef,
    containerRef,
    undoStack,
    undoIndex,
    viewportRef
  );

  const {
    extractCenterline,
    selectTwoPoints,
    shortestPath,
    currentIndex,
    setCurrentIndex,
    moveCameraToIndex,
    autoMoveCamera,
    stopAutoMoveCamera,
    pathSliderValue,
    setPathSliderValue,
    crossSectionPoints,
    applySectionViewAtIndex,
    removeSectionViewHelper,
    setIsCenterlineMode,
    isCenterlineMode,
    setIsCenterlineMeshSelectMode,
    isCenterlineMeshSelectMode,
  } = useCenterline(
    stlURLs,
    setProcessing,
    sceneRef,
    modelRef,
    cameraRef,
    containerRef,
    controlsRef,
    viewportRef
  );

  const {
    isScalpelMode,
    setIsScalpelMode,
    setDrawPoints,
    cutHeight,
    setCutHeight,
    modifyGeometryAndAddToScene,
    clearDrawPoints,
    performScalpelAutoMultiple,
    undoScalpel,
  } = useScalpelMode(
    sceneRef,
    cameraRef,
    containerRef,
    modelRef,
    controlsRef,
    selectedModels,
    undoStack,
    undoIndex,
    setProcessing,
    viewportRef
  );
  //TODO Fix this naming

  const {
    isStentMode,
    setIsStentMode,
    transformMode,
    setTransformMode,
    upperDiameter,
    setUpperDiameter,
    innerDiameter,
    setInnerDiameter,
    lowerDiameter,
    setLowerDiameter,
    height,
    setHeight,
    updateStentInScene,
    downloadStentSTL,
  } = useStentMode(
    sceneRef,
    cameraRef,
    modelRef,
    containerRef,
    controlsRef,
    viewportRef
  );

  const toggleStentMode = () => {
    if (!isStentMode) {
      setIsAnnotationMode(false);
      setIsDistanceMeasuringMode(false);
      setIsScalpelMode(false);
      setIsDrillMode(false);
      setIsCameraOrientationMode(false);
      setIsSurgeryPosMode(false);
      setIsSectionViewModeSimple(false);
      setIsSectionViewMode(false);
      setIsSectionViewSliderModalOpen(false);
      setIsCenterlineMode(false);
      setIsCameraSliderModalOpen(false);
      setIsPointLinkingMode(false);
      setIsExtractMode(false);
      try {
        clearExtractDraw();
        setIsStentMode(false);
        setTransformMode("off");
      } catch (e) { }
      setIsStentMode(true);
    } else {
      setIsStentMode(false);
      setTransformMode("off");
    }
  };

  const { isFullscreen, toggleFullScreen } = useFullScreen(rootWrapperRef);

  const {
    resetCamera,
    setHomeCameraView,
    goHomeCameraView,
    homeOrientation,
    isCameraOrientationMode,
    setIsCameraOrientationMode,
    isCameraRotationLocked,
    toggleCameraRotationLock,
    goTopView,
    goBottomView,
    goLeftView,
    goRightView,
    goFrontView,
    goBackView,
  } = useCamera(
    cameraRef,
    controlsRef,
    modelRef,
    initialCameraPosition,
    initialCameraTarget
  );

  const {
    pathPoints,
    totalDistance,
    polygonArea,
    savedMeasurements,
    isDistanceMeasuringMode,
    setIsDistanceMeasuringMode,
    closePolygon,
    clearMeasurements,
    handleSaveMeasurements,
    handleDeleteMeasurement,
    handleSaveMeasurementText,
    handleMeasurementShowAndHide,
    setIsMeasurementModalOpen,
    isMeasurementModalOpen,
    measuredAngle,
    measureAngle,
    selectedMeshVolume,
    calculateMeshVolume,
    clearVolumeMeasurement,
    isVolumeSelectionMode,
    toggleVolumeSelectionMode,
  } = useMeasurement(
    sceneRef,
    cameraRef,
    modelRef,
    containerRef,
    id,
    measurements,
    undoStack,
    undoIndex,
    undoMeasurementStack,
    thisCase.isDemo,
    viewportRef
  );

  const {
    pins,
    tempAnnotationDetails,
    isAnnotationModalOpen,
    isAnnotationMode,
    setIsAnnotationMode,
    setIsAnnotationModalOpen,
    setTempAnnotationDetails,
    handleSaveAnnotation,
    handleDeleteAnnotation,
    handleSaveAnnotationText,
    setPins,
  } = useAnnotations(
    sceneRef,
    cameraRef,
    containerRef,
    controlsRef,
    modelRef,
    id,
    annotations,
    undoStack,
    undoIndex,
    thisCase.isDemo,
    viewportRef
  );

  const {
    goToSurgeryPosition,
    saveSurgeryPosition,
    createAndSaveSurgeryPos,
    deleteSurgeryPos,
    isSurgeryPosMode,
    setIsSurgeryPosMode,
    selectedSurgeryPosId,
    setSelectedSurgeryPosId,
    isAddSurgeryPosModalOpen,
    setIsAddSurgeryPosModalOpen,
  } = useSurgeryPos(
    cameraRef,
    controlsRef,
    modelRef,
    undoStack,
    undoIndex,
    thisCase,
    performDrillAuto,
    performScalpelAutoMultiple,
    handleOpacityChange,
    handleVisibilityChange
  );

  const {
    isSectionViewMode,
    sectionViewMode,
    setIsSectionViewMode,
    setSectionViewMode,
    applySectionView,
    planeOffset,
    setPlaneOffset,
    showHelper,
    setShowHelper,
    setInvertClip,
  } = useSectionView(modelRef, sceneRef);

  const {
    isSectionViewModeSimple,
    sectionViewModeSimple,
    setIsSectionViewModeSimple,
    setSectionViewModeSimple,
    applySectionViewSimple,
    planeOffsetSimple,
    setPlaneOffsetSimple,
    showHelperSimple,
    setShowHelperSimple,
    setInvertClipSimple,
  } = useSectionViewSimple(modelRef, sceneRef);

  // 👇 NEW: initialize the point-linking hook with the same refs as annotations
  // inside ModelViewer component body
  const {
    isPointLinkMode: isPointLinkingMode,
    setIsPointLinkMode: setIsPointLinkingMode,
  } = usePointLinking({
    sceneRef,
    cameraRef,
    containerRef,
    modelRef,
    viewportRef,
    onNavigate: ({ i, j, k, world }) => {
      // optional: also align your section view plane here, or fly camera, etc.
      // e.g., console.log("Picked IJK:", i, j, k, "at world", world);
    },
  });

  // pull the right things from the hook
  const {
    isExtractMode,
    setIsExtractMode,
    setDrawPoints: setExtractDrawPoints,
    cutHeight: extractHeight,
    setCutHeight: setExtractHeight,
    extractColor,
    setExtractColor,
    extractedName,
    setExtractedName,
    applyExtract,
    clearDrawPoints: clearExtractDraw,
  } = useExtractMode(
    sceneRef,
    cameraRef,
    modelRef,
    selectedModels,
    setProcessing,
    containerRef,
    controlsRef,
    viewportRef
  );

  function setRenderOrderRecursive(obj, order) {
    obj.traverse((o) => {
      if (o.isMesh) o.renderOrder = order;
    });
  }

  // only URLs affect reloads
  const urlsSignature = useMemo(
    () =>
      (stlURLs || [])
        .map((s) => s.url)
        .sort()
        .join(";"),
    [stlURLs]
  );

  const viewerModelGroups = useMemo(() => {
    const allGroups = modelGroups || {};
    const userId = user?.currentUser?.id;

    if (userId && allGroups[userId]) {
      return allGroups[userId];
    }
    return allGroups["common"] || [];
  }, [modelGroups, user?.currentUser?.id]);

  const frameCameraToModel = useCallback(() => {
    if (!cameraRef.current || !modelRef.current || !controlsRef.current) return;
    const bbox = new THREE.Box3().setFromObject(modelRef.current);
    const center = bbox.getCenter(new THREE.Vector3());
    const size = bbox.getSize(new THREE.Vector3());
    const maxDim = Math.max(size.x, size.y, size.z) || 1;
    const distance = maxDim * 2;
    cameraRef.current.up.set(0, 0, 1);
    cameraRef.current.position.set(center.x, center.y - distance, center.z);
    controlsRef.current.target.copy(center);
    cameraRef.current.lookAt(center);
    controlsRef.current.update();
    cameraRef.current.updateProjectionMatrix();
    gizmoRef.current?.cameraUpdate();
  }, []);

  const handleUndo = useCallback(() => {
    if (undoIndex.current > 0) {
      const action = undoStack.current[undoIndex.current - 1];

      if (action && action.type === "drill") {
        undoDrill(action.geometryId, action.originalGeometry);
      }

      if (action && action.type === "annotation") {
        if (action.action === "save") {
          const pinIndex = pins.findIndex(
            (pin) => pin.id === action.annotationId
          );
          handleDeleteAnnotation(pinIndex, action.annotationId, false);
        }
        if (action.action === "delete") {
          handleSaveAnnotation(
            action.annotationData.annotation,
            false,
            action.annotationData.coordinates,
            action.annotationId
          );
        }
      }

      if (action && action.type === "measurement") {
        if (action.action === "save") {
          const measurementIndex = savedMeasurements.findIndex(
            (m) => m.id === action.measurementId
          );
          if (measurementIndex !== -1) {
            handleDeleteMeasurement(measurementIndex, false);
          }
        }
        if (action.action === "delete") {
          handleSaveMeasurements(null, action.measurementData, false);
        }
      }

      if (action && action.type === "scalpel") {
        undoScalpel(action);
      }

      undoIndex.current -= 1;
    }
  }, [
    undoStack,
    undoIndex,
    undoDrill,
    pins,
    handleDeleteAnnotation,
    handleSaveAnnotation,
    savedMeasurements,
    handleDeleteMeasurement,
    handleSaveMeasurements,
    undoScalpel,
  ]);

  const handleRedo = useCallback(() => {
    if (undoIndex.current < undoStack.current.length) {
      const action = undoStack.current[undoIndex.current];

      if (action && action.type === "drill") {
        redoDrill(action.geometryId, action.modifiedGeometry);
      }

      if (action && action.type === "annotation") {
        if (action.action === "save") {
          handleSaveAnnotation(
            action.annotationData.annotation,
            false,
            action.annotationData.coordinates,
            action.annotationId
          );
        }
        if (action.action === "delete") {
          const pinIndex = pins.findIndex(
            (pin) => pin.id === action.annotationId
          );
          handleDeleteAnnotation(pinIndex, action.annotationId, false);
        }
      }

      if (action && action.type === "measurement") {
        if (action.action === "save") {
          handleSaveMeasurements(null, action.measurementData, false);
        }
        if (action.action === "delete") {
          const measurementIndex = savedMeasurements.findIndex(
            (m) => m.id === action.measurementId
          );
          if (measurementIndex !== -1) {
            handleDeleteMeasurement(measurementIndex, false);
          }
        }
      }

      if (action && action.type === "scalpel") {
        performScalpelAutoMultiple(action);
      }
      undoIndex.current += 1;
    }
  }, [
    undoStack,
    undoIndex,
    redoDrill,
    pins,
    handleDeleteAnnotation,
    handleSaveAnnotation,
    savedMeasurements,
    handleDeleteMeasurement,
    handleSaveMeasurements,
    performScalpelAutoMultiple,
  ]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (!(event.ctrlKey || event.metaKey)) return;
      if (event.repeat) return;

      const t = event.target;
      if (
        t?.tagName === "INPUT" ||
        t?.tagName === "TEXTAREA" ||
        t?.tagName === "SELECT" ||
        t?.isContentEditable
      )
        return;

      const h = hotkeyHandlersRef.current;
      if (!h) return;

      const key = event.key.toLowerCase();
      switch (key) {
        case "z":
          event.preventDefault();
          h.undo();
          break;
        case "y":
          event.preventDefault();
          h.redo();
          break;
        case "a":
          event.preventDefault();
          h.annot();
          break;
        case "m":
          event.preventDefault();
          h.measure();
          break;
        // case "s":
        //   event.preventDefault();
        //   h.scalpel();
        //   break;
        // case "d":
        //   event.preventDefault();
        //   h.drill();
        //   break;
        case "v":
          event.preventDefault();
          if (dicomBounds === null) h.sectionSimple();
          else h.section();
          break;
        case "o":
          event.preventDefault();
          h.camOrient();
          break;
        case "p":
          event.preventDefault();
          h.surgPos();
          break;
        case "l":
          event.preventDefault();
          h.pointlink();
          break;
        case "x":
          event.preventDefault();
          h.extract();
          break;
        case "c":
          event.preventDefault();
          h.centerline();
          break;
        default:
          break;
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [dicomBounds]);

  useEffect(() => {
    const handleOrientationChange = () => {
      setIsLandscape(window.innerWidth > window.innerHeight);
    };

    window.addEventListener("resize", handleOrientationChange);
    window.addEventListener("orientationchange", handleOrientationChange);

    return () => {
      window.removeEventListener("resize", handleOrientationChange);
      window.removeEventListener("orientationchange", handleOrientationChange);
    };
  }, []);

  useEffect(() => {
    const g = gizmoRef.current;
    if (!g) return;

    g.enabled = !!(isFullScreen && !isMobile); // disables interaction and we'll skip render
    g.domUpdate(); // refresh layout/visibility if CSS changes
  }, [isFullScreen, isMobile]);

  useEffect(() => {
    if (isMobile || !isFullScreen) {
      // Reset viewport to full container when not in fullscreen desktop mode
      if (containerRef.current) {
        viewportRef.current = {
          left: 0,
          top: 0,
          width: containerRef.current.clientWidth,
          height: containerRef.current.clientHeight,
        };
      }
      return;
    }

    const handleLayoutResize = () => {
      if (!containerRef.current || !rendererRef.current || !cameraRef.current) return;
      const fullWidth = containerRef.current.clientWidth;
      const h = containerRef.current.clientHeight;

      // Always keep renderer at full size (so mouse coordinates work correctly)
      rendererRef.current.setSize(fullWidth, h);

      if (isSidebarOpen) {
        // Sidebar is 320px on desktop (w-80 = 20rem = 320px)
        const sidebarWidth = 320;
        const visibleWidth = fullWidth - sidebarWidth;

        // Enable scissor test to clip rendering to visible area
        rendererRef.current.setScissorTest(true);
        rendererRef.current.setScissor(0, 0, visibleWidth, h);
        rendererRef.current.setViewport(0, 0, visibleWidth, h);

        // Adjust camera aspect ratio to match visible area
        cameraRef.current.aspect = visibleWidth / h;

        // Update viewport ref for mouse coordinate calculations
        viewportRef.current = {
          left: 0,
          top: 0,
          width: visibleWidth,
          height: h,
        };
      } else {
        // Disable scissor test - render to full canvas
        rendererRef.current.setScissorTest(false);
        rendererRef.current.setViewport(0, 0, fullWidth, h);

        cameraRef.current.aspect = fullWidth / h;

        // Update viewport ref for mouse coordinate calculations
        viewportRef.current = {
          left: 0,
          top: 0,
          width: fullWidth,
          height: h,
        };
      }

      cameraRef.current.updateProjectionMatrix();
      controlsRef.current?.handleResize?.();
      gizmoRef.current?.update();
    };

    // Run once immediately
    handleLayoutResize();

    // Observe layout changes securely
    const ro = new ResizeObserver(() => {
      handleLayoutResize();
    });
    if (containerRef.current) {
      ro.observe(containerRef.current);
    }

    return () => ro.disconnect();
  }, [isSidebarOpen, isMobile, isFullScreen]);


  // ----- INITIAL SCENE / RENDERER / CONTROLS -----
  useEffect(() => {
    const scene = new THREE.Scene();
    sceneRef.current = scene;
    scene.add(modelRef.current);

    const ambientLight = new THREE.AmbientLight(0xffffff, 0.25);
    ambientLight.layers.enable(LAYER_OPAQUE);
    ambientLight.layers.enable(LAYER_TRANSPARENT);
    scene.add(ambientLight);

    const cameraLight = new THREE.DirectionalLight(0xffffff, 2);
    cameraLight.castShadow = true;
    cameraLight.shadow.mapSize.set(2048, 2048);
    cameraLight.shadow.bias = -0.0005;
    cameraLight.shadow.normalBias = 0.02;
    const camSize = 200;
    Object.assign(cameraLight.shadow.camera, {
      left: -camSize,
      right: camSize,
      top: camSize,
      bottom: -camSize,
      near: 0.5,
      far: 2000,
    });
    cameraLight.layers.enable(LAYER_OPAQUE);
    cameraLight.layers.enable(LAYER_TRANSPARENT);
    scene.add(cameraLight);

    const fov = 30;
    const width = containerRef.current?.clientWidth ?? 1;
    const height = containerRef.current?.clientHeight ?? 1;
    const camera = new THREE.PerspectiveCamera(fov, width / height, 0.1, 10000);
    cameraRef.current = camera;

    const renderer = new THREE.WebGLRenderer({
      alpha: true,
      preserveDrawingBuffer: true,
      antialias: true,
    });
    renderer.setPixelRatio(window.devicePixelRatio || 1);
    renderer.setSize(width, height);
    renderer.autoClear = false;
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    THREE.ColorManagement.enabled = true;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.1;
    renderer.localClippingEnabled = true;
    renderer.setClearColor(0xfafafa, 1);
    renderer.sortObjects = true;
    rendererRef.current = renderer;
    // put this near your init code (after renderer exists)
    // const veinTexture = new THREE.TextureLoader().load("/output_1.png");
    // veinTexture.wrapS = veinTexture.wrapT = THREE.RepeatWrapping;
    // veinTexture.colorSpace = THREE.SRGBColorSpace;
    // veinTexture.anisotropy =
    //   rendererRef.current?.capabilities.getMaxAnisotropy?.() || 1;
    // scene.userData.veinTexture = veinTexture;

    if (
      containerRef.current &&
      !containerRef.current.contains(renderer.domElement)
    ) {
      containerRef.current.appendChild(renderer.domElement);
    }

    const controls = new TrackballControls(
      cameraRef.current,
      renderer.domElement
    );
    controls.rotateSpeed = 2.4;
    controls.zoomSpeed = 1.2;
    controls.panSpeed = 0.9;
    controls.staticMoving = true;
    //controls.dynamicDampingFactor = 0.3;
    controlsRef.current = controls;
    controls.handleResize?.();
    frameCameraToModel();

    // View cube gizmo overlay (bottom-right)
    const gizmo = new ViewportGizmo(cameraRef.current, rendererRef.current, {
      container: containerRef.current,
      type: "cube",
      placement: "bottom-right",
      offset: { right: 340, bottom: 8 },
      size: 120,
      animated: true,
      speed: 1.0,

      // Z-up friendly labels (no color overrides)
      top: { label: "   P   " }, // +Z
      bottom: { label: "   A   " }, // -Z
      front: { label: "   S   " }, // +Y
      back: { label: "   I   " }, // -Y
      right: { label: "   R   " }, // +X
      left: { label: "   L   " }, // -X

      // nicer but subtle styling (no plane colors)
      className: "viewer-gizmo",
      font: { family: "Inter, system-ui, sans-serif", weight: 600 },
      background: { enabled: true, color: "#fff", opacity: 0.85 },
      edges: { enabled: true, opacity: 0.25, smoothness: 8, radius: 0.18 },
      //corners: { enabled: false },
    });

    // let the gizmo link itself to controls safely
    gizmo.target.copy(controlsRef.current.target);
    gizmo.attachControls(controlsRef.current);
    gizmo.enabled = isFullScreen && !isMobile;
    gizmo.domUpdate?.();

    gizmoRef.current = gizmo;

    const animate = (now) => {
      rafIdRef.current = requestAnimationFrame(animate);
      myTweenGroup.update(now);
      controls.update();

      cameraLight.position.copy(camera.position);
      const dir = new THREE.Vector3();
      camera.getWorldDirection(dir);
      cameraLight.target.position.copy(camera.position.clone().add(dir));
      cameraLight.target.updateMatrixWorld();

      const scene_ = sceneRef.current;
      const cam_ = cameraRef.current;
      const renderer_ = rendererRef.current;
      if (!scene_ || !cam_ || !renderer_) return;

      renderer_.clear();
      cam_.layers.set(LAYER_OPAQUE);
      renderer_.render(scene_, cam_);
      renderer_.clearDepth();
      cam_.layers.set(LAYER_TRANSPARENT);
      renderer_.render(scene_, cam_);
      if (gizmoRef.current?.enabled) {
        //gizmoRef.current.cameraUpdate(); // sync orientation
        gizmoRef.current.render(); // then draw gizmo
      }
    };
    rafIdRef.current = requestAnimationFrame(animate);

    const handleResize = () => {
      if (!containerRef.current || !rendererRef.current || !cameraRef.current)
        return;
      const w = containerRef.current.clientWidth;
      const h = containerRef.current.clientHeight;
      rendererRef.current.setSize(w, h);
      cameraRef.current.aspect = w / h;
      cameraRef.current.updateProjectionMatrix();
      controlsRef.current?.handleResize?.();
      gizmoRef.current?.update();

      // Update viewport ref (will be adjusted by sidebar effect if needed)
      viewportRef.current = {
        left: 0,
        top: 0,
        width: w,
        height: h,
      };
    };
    resizeHandlerRef.current = handleResize;
    window.addEventListener("resize", handleResize);

    return () => {
      cancelAnimationFrame(rafIdRef.current);
      window.removeEventListener("resize", resizeHandlerRef.current);
      try {
        controls.dispose();
      } catch { }
      try {
        gizmoRef.current?.dispose();
      } catch { }
      gizmoRef.current = null;

      scene.traverse((obj) => {
        if (obj.isMesh || obj.isLine || obj.isPoints) {
          obj.geometry?.dispose?.();
          const mats = Array.isArray(obj.material)
            ? obj.material
            : [obj.material];
          mats.filter(Boolean).forEach((mat) => {
            Object.values(mat).forEach((v) => {
              if (v?.isTexture || v?.isRenderTarget) v.dispose();
            });
            mat.dispose?.();
          });
        }
      });

      try {
        renderer.dispose();
      } catch { }
      try {
        renderer.forceContextLoss();
      } catch { }
      const canvas = renderer.domElement;
      if (canvas?.parentNode) canvas.parentNode.removeChild(canvas);

      myTweenGroup.removeAll();

      rendererRef.current = null;
      controlsRef.current = null;
      cameraRef.current = null;
      sceneRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // mount once

  // ---------- CASE LOAD: dispose old meshes, load new STLs ----------
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    const modelRoot = modelRef.current;
    if (!sceneRef.current || !modelRoot) return;

    const { track, dispose } = makeResourceTracker();

    [...modelRoot.children].forEach((child) => {
      modelRoot.remove(child);
      track(child);
    });
    dispose();

    setNumOfLoadedModels(0);

    if (!stlURLs || stlURLs.length === 0) {
      setIsModelLoading(false);
      frameCameraToModel();
      return;
    }

    setIsModelLoading(true);
    const loader = new STLLoader();
    let isCancelled = false;
    const tempMeshes = new Array(stlURLs.length);

    const loadOne = (stl, index) =>
      new Promise((resolve) => {
        loader.load(
          stl.url,
          (geometry) => {
            if (isCancelled) {
              geometry.dispose();
              return resolve();
            }
            const material = new THREE.MeshStandardMaterial({
              color: stl.color || 0xd3d3d3,
              transparent: true,
              opacity: 1,
              roughness: 0.6,
              metalness: 0.0,
              depthWrite: true,
              depthTest: true,
              clippingPlanes: [],
              side: THREE.DoubleSide,
            });

            const mesh = new THREE.Mesh(geometry, material);
            mesh.userData.sourceKey = stl.url;
            mesh.userData.originalColor = stl.color || "#d3d3d3";
            mesh.name = stl.name;
            mesh.renderOrder = stl.renderOrder ?? 0;
            mesh.isWatertight = !!stl.isWatertight;
            mesh.castShadow = true;
            mesh.receiveShadow = true;
            mesh.layers.enable(LAYER_OPAQUE);
            mesh.layers.disable(LAYER_TRANSPARENT);

            track(mesh);
            tempMeshes[index] = mesh;
            setNumOfLoadedModels((n) => n + 1);
            resolve();
          },
          undefined,
          (err) => {
            console.error("STL load error:", stl.url, err);
            resolve();
          }
        );
      });

    (async () => {
      await Promise.all(stlURLs.map(loadOne));
      if (isCancelled) return;
      tempMeshes.forEach((m) => m && modelRoot.add(m));
      setIsModelLoading(false);
      frameCameraToModel();

      const cam = cameraRef.current;
      const ctr = controlsRef.current;
      if (cam && ctr) {
        setInitialCameraPosition({
          x: cam.position.x,
          y: cam.position.y,
          z: cam.position.z,
          initialQuaternion: cam.quaternion.clone(),
          up: cam.up.clone(),
        });
        setInitialCameraTarget({
          x: ctr.target.x,
          y: ctr.target.y,
          z: ctr.target.z,
        });
      }
    })();

    return () => {
      isCancelled = true;
      [...modelRoot.children].forEach((child) => {
        modelRoot.remove(child);
        track(child);
      });
      dispose();
    };
  }, [urlsSignature, frameCameraToModel]);
  /* eslint-enable react-hooks/exhaustive-deps */

  // ---- APPEARANCE-ONLY UPDATE (no reload) ----
  useEffect(() => {
    const root = modelRef.current;
    if (!root) return;

    const byKey = new Map();
    root.children.forEach((obj) => {
      const k = obj?.userData?.sourceKey;
      if (k) byKey.set(k, obj);
    });

    // 🔹 Figure out which indices belong to groups
    const allModelGroups = selectedCaseDetail?.modelGroups || {};
    const userId = user?.currentUser?.id;
    const groups =
      (userId && Array.isArray(allModelGroups[userId])
        ? allModelGroups[userId]
        : allModelGroups["common"]) || [];

    const groupedIndices = new Set();
    groups.forEach((g) => {
      g.indices?.forEach((idx) => groupedIndices.add(idx));
    });

    (stlURLs || []).forEach((s, index) => {
      const mesh = byKey.get(s.url);
      if (!mesh) return;

      // 🔸 If this mesh is in a group, DO NOT touch its color here.
      // Group color logic is centralized in the other effect.
      if (!groupedIndices.has(index)) {
        if (s.color && mesh.material?.color) {
          const next = new THREE.Color(s.color);
          if (!mesh.material.color.equals(next)) {
            mesh.material.color.copy(next);
            mesh.material.needsUpdate = true;
          }
        }
      }

      // We still allow renderOrder to be updated for everyone
      if (
        typeof s.renderOrder === "number" &&
        mesh.renderOrder !== s.renderOrder
      ) {
        mesh.renderOrder = s.renderOrder;
      }
    });
  }, [stlURLs, selectedCaseDetail?.modelGroups, user?.currentUser?.id]);

  // ---- APPLY GROUP COLORS TO MESHES ----
  useEffect(() => {
    const root = modelRef.current;
    if (!root || !root.children.length || isModelLoading) return;

    const allModelGroups = selectedCaseDetail?.modelGroups || {};
    const userId = user?.currentUser?.id;

    const groups =
      (userId && Array.isArray(allModelGroups[userId])
        ? allModelGroups[userId]
        : allModelGroups["common"]) || [];

    groups.forEach((group) => {
      if (!group.indices || !group.color) return;

      const groupColor = new THREE.Color(group.color);

      group.indices.forEach((idx) => {
        const mesh = root.children[idx];
        if (!mesh?.material?.color) return;

        if (!mesh.material.color.equals(groupColor)) {
          mesh.material.color.copy(groupColor);
          mesh.material.needsUpdate = true;
        }
      });
    });
  }, [selectedCaseDetail?.modelGroups, user?.currentUser?.id, isModelLoading]);
  const handleCheckboxChange = (checked, id) => {
    setPins((prevPins) => {
      return prevPins.map((pin) => {
        if (pin.id === id) {
          return { ...pin, show: checked };
        }
        return pin;
      });
    });
  };

  const handleMoveToStructure = (index) => {
    const thisModel = modelRef.current.children[index];

    thisModel.geometry.computeBoundingBox();
    const boundingBox = thisModel.geometry.boundingBox;

    const thisModelCenter = new THREE.Vector3();
    boundingBox.getCenter(thisModelCenter);
    thisModel.localToWorld(thisModelCenter);

    moveToAnnotation(thisModelCenter);
  };

  const handleColorChange = (index, color) => {
    if (
      sceneRef.current &&
      modelRef.current &&
      modelRef.current.children[index]
    ) {
      modelRef.current.children[index].material.color.set(color);
    }
  };

  const handleColorSave = (index, color) => {
    // 🔹 1) Update the mesh's "originalColor" so group logic uses the new value
    const mesh = modelRef.current?.children?.[index];
    if (mesh) {
      if (!mesh.userData) mesh.userData = {};
      mesh.userData.originalColor = color;
    }

    // 🔹 2) Skip Firebase writes for demo cases
    if (!thisCase.isDemo) {
      const modelDbRef = ref(database, `caseDetails/${id}/modelInfos/${index}`);
      update(modelDbRef, { color });
    }

    // 🔹 3) Update Redux modelInfos so the rest of the app sees the new color
    const updatedModelInfos = [...(selectedCaseDetail?.modelInfos || [])];
    updatedModelInfos[index] = {
      ...updatedModelInfos[index],
      color,
    };

    dispatch(
      updateModelInfos({
        caseId: id,
        modelInfos: updatedModelInfos,
      })
    );
  };

  const handleGroupColorSave = (groupIndex, color) => {
    if (!selectedCaseDetail) return;

    const allModelGroups = selectedCaseDetail.modelGroups || {};
    const userId = user?.currentUser?.id;

    // Decide whether this viewer is using personal or common groups,
    // same logic you already use when reading modelGroups for the sidebar.
    const ownerKey =
      userId && Array.isArray(allModelGroups[userId]) ? userId : "common";

    const groupsForOwner = allModelGroups[ownerKey] || [];
    if (!groupsForOwner[groupIndex]) return;

    // 1) Persist to Firebase (unless it's a demo case)
    if (!thisCase.isDemo) {
      const groupDbRef = ref(
        database,
        `caseDetails/${id}/modelGroups/${ownerKey}/${groupIndex}`
      );

      update(groupDbRef, { color }).catch((err) => {
        console.error("Error updating group color:", err);
      });
    }

    // 2) Build a NEW modelGroups object immutably for Redux
    const updatedGroupsForOwner = groupsForOwner.map((g, idx) =>
      idx === groupIndex ? { ...g, color } : g
    );

    const updatedModelGroups = {
      ...allModelGroups,
      [ownerKey]: updatedGroupsForOwner,
    };

    // 3) Dispatch a Redux action that updates modelGroups
    // ⚠️ Replace `updateModelGroups` with whatever action you actually have.
    dispatch(
      updateModelGroups({
        caseId: id,
        modelGroups: updatedModelGroups,
      })
    );
  };

  // ========== Model Grouping Modal Handlers ==========
  const handleOpenGroupingModal = () => {
    setShowGroupingModal(true);
    setIsSidebarOpen(false); // Close sidebar for better modal visibility
  };

  const handleCloseGroupingModal = () => {
    setShowGroupingModal(false);
  };

  const handleSaveGroups = async (groups, applyToAllUsers) => {
    try {
      await handleSaveModelGroups(groups, applyToAllUsers);
      setShowGroupingModal(false);
    } catch (error) {
      console.error("Error saving groups:", error);
      alert("Failed to save groups. Please try again.");
    }
  };

  const calculateOptimalCameraPosition = (annotationPosition) => {
    const boundingBox = new THREE.Box3().setFromObject(modelRef.current);
    const center = boundingBox.getCenter(new THREE.Vector3());
    const size = boundingBox.getSize(new THREE.Vector3());
    const maxDimension = Math.max(size.x, size.y, size.z);

    const distance = maxDimension;
    const height = maxDimension;

    const direction = new THREE.Vector3()
      .subVectors(annotationPosition, center)
      .normalize();
    const newPosition = new THREE.Vector3().addVectors(
      annotationPosition,
      direction.multiplyScalar(distance)
    );
    newPosition.y += height;

    return newPosition;
  };

  //TODO Adding this to useAnnitations hook complicates stuff. It breaks some ref somehow and animation does not work as intented.
  function moveToAnnotation(annotationPosition) {
    const camera = cameraRef.current;
    const controls = controlsRef.current;
    if (!camera || !controls) return;

    const optimalCameraPosition =
      calculateOptimalCameraPosition(annotationPosition);

    const from = {
      camX: camera.position.x,
      camY: camera.position.y,
      camZ: camera.position.z,
      tgtX: controls.target.x,
      tgtY: controls.target.y,
      tgtZ: controls.target.z,
    };

    const to = {
      camX: optimalCameraPosition.x,
      camY: optimalCameraPosition.y,
      camZ: optimalCameraPosition.z,
      tgtX: annotationPosition.x,
      tgtY: annotationPosition.y,
      tgtZ: annotationPosition.z,
    };

    new Tween(from, myTweenGroup)
      .to(to, 1000)
      .easing(Easing.Quadratic.Out)
      .onUpdate(() => {
        camera.position.set(from.camX, from.camY, from.camZ);
        controls.target.set(from.tgtX, from.tgtY, from.tgtZ);
        controls.update();
        gizmoRef.current?.cameraUpdate();
      })
      .onComplete(() => {
        controls.update();
      })
      .start();
  }

  const stopVideoRecording = useCallback(() => {
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }

    if (
      videoRecorderRef.current &&
      videoRecorderRef.current.state === "recording"
    ) {
      videoRecorderRef.current.stop();
    }
  }, []);

  const toggleVideoRecording = useCallback(() => {
    if (isRecording) {
      // Stop recording
      stopVideoRecording();
      setIsRecording(false);
    } else {
      // Start recording
      if (!rendererRef.current) {
        setVideoStatus("error");
        setTimeout(() => setVideoStatus(null), 2000);
        return;
      }

      try {
        const canvas = rendererRef.current.domElement;
        const stream = canvas.captureStream(30); // 30 fps

        videoChunksRef.current = [];

        // Try MP4 first, fallback to WebM if not supported
        let mimeType = "video/webm;codecs=vp9";
        let fileExtension = "webm";

        if (MediaRecorder.isTypeSupported("video/mp4")) {
          mimeType = "video/mp4";
          fileExtension = "mp4";
        } else if (MediaRecorder.isTypeSupported("video/webm;codecs=h264")) {
          mimeType = "video/webm;codecs=h264";
          fileExtension = "webm";
        }

        const mediaRecorder = new MediaRecorder(stream, {
          mimeType: mimeType,
          videoBitsPerSecond: 5000000, // 5 Mbps for good quality
        });

        // Store file extension for later use
        mediaRecorder.fileExtension = fileExtension;

        mediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            videoChunksRef.current.push(event.data);
          }
        };

        mediaRecorder.onstop = () => {
          const ext = mediaRecorder.fileExtension || "webm";
          const mimeTypeForBlob = ext === "mp4" ? "video/mp4" : "video/webm";
          const blob = new Blob(videoChunksRef.current, {
            type: mimeTypeForBlob,
          });
          const now = new Date();
          const timestamp = now
            .toISOString()
            .slice(0, 19)
            .replace(/[:.]/g, "-");
          const filename = `model-video-${timestamp}.${ext}`;

          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.download = filename;
          link.href = url;
          link.style.display = "none";

          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          setTimeout(() => URL.revokeObjectURL(url), 100);

          setVideoStatus("success");
          setTimeout(() => {
            setVideoStatus(null);
            setRecordingSeconds(0);
          }, 2000);
        };

        videoRecorderRef.current = mediaRecorder;
        mediaRecorder.start();
        setIsRecording(true);
        setVideoStatus("recording");
        setRecordingSeconds(0);

        // Timer update every second
        recordingTimerRef.current = setInterval(() => {
          setRecordingSeconds((prev) => {
            const newSeconds = prev + 1;
            // Auto-stop at 30 seconds
            if (newSeconds >= 30) {
              stopVideoRecording();
              setIsRecording(false);
              return 30;
            }
            return newSeconds;
          });
        }, 1000);
      } catch (error) {
        console.error("Video recording error:", error);
        setVideoStatus("error");
        setTimeout(() => {
          setVideoStatus(null);
          setRecordingSeconds(0);
        }, 2000);
      }
    }
  }, [isRecording, stopVideoRecording]);

  const handleCancelAnnotation = () => {
    setIsAnnotationModalOpen(false);
    setTempAnnotationDetails(null);
  };

  const toggleAnnotationMode = () => {
    if (!isAnnotationMode) {
      // turn off all others first
      setIsDistanceMeasuringMode(false);
      setIsScalpelMode(false);
      clearDrawPoints();
      setIsCameraOrientationMode(false);
      clearMeasurements();
      setIsDrillMode(false);
      setIsSectionViewModeSimple(false);
      setIsSurgeryPosMode(false);
      setIsSectionViewMode(false);
      setIsPointLinkingMode(false);
      setIsExtractMode(false);
      clearExtractDraw();
      setIsStentMode(false);
      setTransformMode("off");
      setIsSectionViewSliderModalOpen(false);
      setIsCenterlineMode(false);
      setIsSectionViewSliderModalOpen(false);
      setIsCameraSliderModalOpen(false);
      setIsAnnotationMode(true);
      setActiveTab("annotations");
    } else {
      setIsAnnotationMode(false);
    }
  };

  const toggleDistanceMeasuringMode = () => {
    if (!isDistanceMeasuringMode) {
      setIsAnnotationMode(false);
      setIsScalpelMode(false);
      setIsCameraOrientationMode(false);
      clearDrawPoints();
      setIsDrillMode(false);
      setIsSurgeryPosMode(false);
      setIsSectionViewModeSimple(false);
      setIsSectionViewMode(false);
      setIsPointLinkingMode(false);
      setIsExtractMode(false);
      clearExtractDraw();
      setIsStentMode(false);
      setTransformMode("off");
      setIsSectionViewSliderModalOpen(false);
      setIsCenterlineMode(false);
      setIsSectionViewSliderModalOpen(false);
      setIsCameraSliderModalOpen(false);
      setIsDistanceMeasuringMode(true);
      setActiveTab("measurements");
    } else {
      setIsDistanceMeasuringMode(false);
      clearMeasurements();
    }
  };

  const toggleScalpelMode = () => {
    if (!isScalpelMode) {
      setIsAnnotationMode(false);
      setIsDistanceMeasuringMode(false);
      setIsCameraOrientationMode(false);
      setIsSurgeryPosMode(false);
      clearMeasurements();
      setIsDrillMode(false);
      setIsSectionViewMode(false);
      setIsSectionViewModeSimple(false);
      setIsPointLinkingMode(false);
      setIsExtractMode(false);
      clearExtractDraw();
      setIsStentMode(false);
      setTransformMode("off");
      setIsSectionViewSliderModalOpen(false);
      setIsCenterlineMode(false);
      setIsSectionViewSliderModalOpen(false);
      setIsCameraSliderModalOpen(false);
      setIsScalpelMode(true);
      setActiveTab("models");
      setDrawPoints([]);
    } else {
      setIsScalpelMode(false);
      clearDrawPoints();
    }
  };

  const toggleDrillMode = () => {
    if (!isDrillMode) {
      setIsAnnotationMode(false);
      setIsDistanceMeasuringMode(false);
      setIsScalpelMode(false);
      setIsCameraOrientationMode(false);
      setIsSurgeryPosMode(false);
      setIsSectionViewMode(false);
      clearDrawPoints();
      setIsSectionViewModeSimple(false);
      clearMeasurements();
      setIsPointLinkingMode(false);
      setIsExtractMode(false);
      setIsSectionViewSliderModalOpen(false);
      setIsCenterlineMode(false);
      setIsSectionViewSliderModalOpen(false);
      setIsCameraSliderModalOpen(false);
      clearExtractDraw();
      setIsStentMode(false);
      setTransformMode("off");
      setIsDrillMode(true);
      setActiveTab("models");
    } else {
      setIsDrillMode(false);
      clearDrawPoints();
    }
  };

  const toggleCameraOrientationMode = () => {
    if (!isCameraOrientationMode) {
      setIsAnnotationMode(false);
      setIsDistanceMeasuringMode(false);
      setIsScalpelMode(false);
      setIsDrillMode(false);
      setIsSurgeryPosMode(false);
      setIsSectionViewModeSimple(false);
      setIsSectionViewMode(false);
      setIsSectionViewSliderModalOpen(false);
      setIsCenterlineMode(false);
      setIsSectionViewSliderModalOpen(false);
      setIsCameraSliderModalOpen(false);
      setIsPointLinkingMode(false);
      setIsExtractMode(false);
      clearExtractDraw();
      setIsStentMode(false);
      setTransformMode("off");
      setIsCameraOrientationMode(true);
    } else {
      setIsCameraOrientationMode(false);
    }
  };

  const toggleSurgeryPosMode = () => {
    if (!isSurgeryPosMode) {
      setIsAnnotationMode(false);
      setIsDistanceMeasuringMode(false);
      setIsScalpelMode(false);
      setIsDrillMode(false);
      setIsCameraOrientationMode(false);
      setIsSectionViewMode(false);
      setIsSectionViewModeSimple(false);
      setIsSectionViewSliderModalOpen(false);
      setIsCenterlineMode(false);
      setIsSectionViewSliderModalOpen(false);
      setIsCameraSliderModalOpen(false);
      setIsPointLinkingMode(false);
      setIsExtractMode(false);
      clearExtractDraw();
      setIsStentMode(false);
      setTransformMode("off");
      setIsSurgeryPosMode(true);
    } else {
      setIsSurgeryPosMode(false);
    }
  };

  const toggleSectionViewMode = () => {
    if (!isSectionViewMode) {
      setIsAnnotationMode(false);
      setIsDistanceMeasuringMode(false);
      setIsScalpelMode(false);
      setIsDrillMode(false);
      setIsCameraOrientationMode(false);
      setIsSurgeryPosMode(false);
      setPlaneOffset(0);
      setSectionViewMode("none");
      applySectionView();
      setShowHelper(false);
      setIsSectionViewSliderModalOpen(false);
      setIsCenterlineMode(false);
      setIsSectionViewSliderModalOpen(false);
      setIsCameraSliderModalOpen(false);
      setIsSectionViewModeSimple(false);
      setIsPointLinkingMode(false);
      setIsExtractMode(false);
      clearExtractDraw();
      setIsStentMode(false);
      setTransformMode("off");
      setIsSectionViewMode(true);
    } else {
      setIsSectionViewMode(false);
    }
  };

  const toggleSectionViewModeSimple = () => {
    if (!isSectionViewModeSimple) {
      setIsAnnotationMode(false);
      setIsDistanceMeasuringMode(false);
      setIsScalpelMode(false);
      setIsDrillMode(false);
      setIsCameraOrientationMode(false);
      setIsSurgeryPosMode(false);
      setPlaneOffset(0);
      setSectionViewMode("none");
      applySectionView();
      setShowHelper(false);
      setIsSectionViewMode(false);
      setIsSectionViewSliderModalOpen(false);
      setIsCenterlineMode(false);
      setIsSectionViewSliderModalOpen(false);
      setIsCameraSliderModalOpen(false);
      setIsPointLinkingMode(false);
      setIsExtractMode(false);
      clearExtractDraw();
      setIsStentMode(false);
      setTransformMode("off");
      setIsSectionViewModeSimple(true);
    } else {
      setIsSectionViewModeSimple(false);
    }
  };

  // 👇 NEW: Point-linking mode toggle (like annotation)
  const togglePointLinkingMode = () => {
    if (!isPointLinkingMode) {
      // kill everything else
      setIsAnnotationMode(false);
      setIsDistanceMeasuringMode(false);
      setIsScalpelMode(false);
      setIsDrillMode(false);
      setIsCameraOrientationMode(false);
      setIsSurgeryPosMode(false);
      setIsSectionViewMode(false);
      setIsSectionViewSliderModalOpen(false);
      setIsCenterlineMode(false);
      setIsSectionViewSliderModalOpen(false);
      setIsCameraSliderModalOpen(false);
      setIsSectionViewModeSimple(false);
      setIsExtractMode(false);
      clearExtractDraw();
      setIsStentMode(false);
      setTransformMode("off");
      clearDrawPoints();
      clearMeasurements();
      // enable
      setIsPointLinkingMode(true);
      setActiveTab("models"); // or wherever you prefer
    } else {
      setIsPointLinkingMode(false);
    }
  };

  const toggleExtractMode = () => {
    if (!isExtractMode) {
      // turn off everything else (mirror your other toggles)
      setIsAnnotationMode(false);
      setIsDistanceMeasuringMode(false);
      setIsScalpelMode(false);
      setIsDrillMode(false);
      setIsCameraOrientationMode(false);
      setIsSurgeryPosMode(false);
      setIsSectionViewMode(false);
      setIsSectionViewSliderModalOpen(false);
      setIsCenterlineMode(false);
      setIsSectionViewSliderModalOpen(false);
      setIsCameraSliderModalOpen(false);
      setIsSectionViewModeSimple(false);
      setIsPointLinkingMode(false);
      // clear other draw states
      clearDrawPoints();
      clearMeasurements();
      // enable extract
      setIsExtractMode(true);
      setActiveTab("models");
      setExtractDrawPoints([]); // start fresh
    } else {
      setIsExtractMode(false);
      clearExtractDraw();
      setIsStentMode(false);
      setTransformMode("off");
    }
  };

  const toggleCenterlineMode = () => {
    if (!isCenterlineMode) {
      setIsAnnotationMode(false);
      setIsDistanceMeasuringMode(false);
      setIsScalpelMode(false);
      setIsDrillMode(false);
      setIsCameraOrientationMode(false);
      setIsSurgeryPosMode(false);
      setIsSectionViewMode(false);
      setIsSectionViewModeSimple(false);
      setIsSectionViewSliderModalOpen(false);
      clearDrawPoints && clearDrawPoints();
      clearMeasurements && clearMeasurements();
      setIsPointLinkingMode(false);
      setIsExtractMode(false);
      clearExtractDraw();
      setIsStentMode(false);
      setTransformMode("off");
      setIsCenterlineMode(true);
    } else {
      setIsCenterlineMeshSelectMode(false);
      setIsCameraSliderModalOpen(false);
      setIsCenterlineMode(false);
    }
  };
  const toggleCenterlineMeshSelectionMode = () => {
    if (!isCenterlineMeshSelectMode) {
      setIsCenterlineMeshSelectMode(true);
    } else {
      setIsCenterlineMeshSelectMode(false);
    }
  };

  hotkeyHandlersRef.current = {
    undo: handleUndo,
    redo: handleRedo,
    annot: toggleAnnotationMode,
    measure: toggleDistanceMeasuringMode,
    scalpel: toggleScalpelMode,
    drill: toggleDrillMode,
    section: toggleSectionViewMode,
    sectionSimple: toggleSectionViewModeSimple,
    camOrient: toggleCameraOrientationMode,
    surgPos: toggleSurgeryPosMode,
    fullscreen: toggleFullScreen,
    pointlink: togglePointLinkingMode,
    extract: toggleExtractMode,
    centerline: toggleCenterlineMode,
  };

  useEffect(() => {
    setIsSidebarOpen(isFullScreen);
  }, [isFullScreen, setIsSidebarOpen]);

  return (
    <div className="relative w-full h-full" ref={rootWrapperRef}>
      <div
        className={`relative overflow-hidden bg-gray-50 ${isFullscreen ? "h-screen w-screen" : "h-full"
          }`}
      >
        <div
          className="relative overflow-hidden bg-gray-50 h-full w-full z-0"
          ref={containerRef}
        >
          {isModelLoading && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="rounded px-3 py-2 bg-white/70 shadow">
                Loading 3D models… ({numOfLoadedModels}/{stlURLs?.length || 0})
              </div>
            </div>
          )}
          {containerRef.current && cameraRef.current && (
            <Pins
              pins={pins}
              cameraRef={cameraRef}
              containerRef={containerRef}
              moveToAnnotation={moveToAnnotation}
            />
          )}

          {/* Undo/Redo buttons for mobile view only */}
          {isMobile && isFullScreen && (
            <div className="absolute top-2 left-16 flex gap-2 z-20 ui-outside-scene">
              <button
                onClick={() => {
                  if (undoIndex.current > 0) {
                    handleUndo();
                  }
                }}
                className="px-3 py-2 rounded text-white transition-colors bg-[var(--main-color)] hover:bg-[var(--main-color-bold)]"
                title="Undo (Ctrl+Z)"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
              <button
                onClick={() => {
                  if (undoIndex.current < undoStack.current.length) {
                    handleRedo();
                  }
                }}
                className="px-3 py-2 rounded text-white transition-colors bg-[var(--main-color)] hover:bg-[var(--main-color-bold)]"
                title="Redo (Ctrl+Y)"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </div>
          )}

          <VideoSnackbar status={videoStatus} seconds={recordingSeconds} />
          {isSidebarOpen && (
            <div className="ui-outside-scene">
              <Tabs
                activeTab={activeTab}
                onTabClick={setActiveTab}
                setIsSidebarOpen={setIsSidebarOpen}
              />
            </div>
          )}
          {!isSidebarOpen && (
            <button
              onClick={() => setIsSidebarOpen(true)}
              className="absolute top-8 right-0 transform -translate-y-1/2 bg-gray-800 text-white p-2 rounded-l ui-outside-scene"
            >
              <ArrowBackIosNewRoundedIcon />
            </button>
          )}
          {!isModelLoading && isSidebarOpen && (
            <div className="ui-outside-scene">
              {activeTab === "models" && (
                <ModelsSidebar
                  modelsRefs={modelRef.current.children}
                  onOpacityChange={handleOpacityChange}
                  onVisibilityChange={handleVisibilityChange}
                  onColorChange={handleColorChange}
                  onColorSave={handleColorSave}
                  onMoveToStructure={handleMoveToStructure}
                  modelGroups={viewerModelGroups}
                  isTargetMeshMode={isTargetMeshMode}
                  toggleTargetMeshMode={toggleTargetMeshMode}
                  targetedIndices={targetedIndices}
                  onGroupColorSave={handleGroupColorSave}
                  onOpenGroupingModal={handleOpenGroupingModal}
                />
              )}
              {activeTab === "annotations" && (
                <AnnotationsSidebar
                  pins={pins}
                  onDelete={handleDeleteAnnotation}
                  onSave={handleSaveAnnotationText}
                  handleCheckboxChange={handleCheckboxChange}
                  onSelect={(pin) => {
                    moveToAnnotation(
                      new THREE.Vector3(
                        pin.coordinates.x,
                        pin.coordinates.y,
                        pin.coordinates.z
                      )
                    );
                  }}
                />
              )}
              {activeTab === "measurements" && (
                <MeasurementsSidebar
                  totalDistance={totalDistance}
                  measurements={savedMeasurements}
                  measuredAngle={measuredAngle}
                  polygonArea={polygonArea}
                  selectedMeshVolume={selectedMeshVolume}
                  onDelete={handleDeleteMeasurement}
                  onSave={handleSaveMeasurementText}
                  handleCheckboxChange={handleMeasurementShowAndHide}
                />
              )}
            </div>
          )}

          {isFullScreen && !isMobile && !showGroupingModal && (
            <OnScreenNav
              controlsRef={controlsRef}
              cameraRef={cameraRef}
              onReset={resetCamera}
              isSidebarOpen={isSidebarOpen}
            />
          )}

          {/* Controls Container */}
          {!(isMobile && !isFullScreen) && (
            <div
              className={`absolute p-2 text-red-800 z-10 text-sm ui-outside-scene ${isMobile && isFullScreen && !isLandscape
                  ? "bottom-2 left-0 right-0 flex justify-center"
                  : isMobile && isFullScreen && isLandscape
                    ? "left-2 top-1/2 -translate-y-1/2"
                    : "bottom-2 md:bottom-32 left-0"
                }`}
            >
              <SideButtonsControls
                isAnnotationMode={isAnnotationMode}
                isDistanceMeasuringMode={isDistanceMeasuringMode}
                isScalpelMode={isScalpelMode}
                isDrillMode={isDrillMode}
                isMobile={isMobile}
                isFullscreen={isFullscreen}
                isFullScreen={isFullScreen}
                toggleAnnotationMode={toggleAnnotationMode}
                toggleDistanceMeasuringMode={toggleDistanceMeasuringMode}
                toggleScalpelMode={toggleScalpelMode}
                toggleDrillMode={toggleDrillMode}
                toggleCameraOrientationMode={toggleCameraOrientationMode}
                toggleSurgeryPosMode={toggleSurgeryPosMode}
                toggleSectionViewMode={toggleSectionViewMode}
                toggleFullScreen={toggleFullScreen}
                toggleVideoRecording={toggleVideoRecording}
                isRecording={isRecording}
                dicomBounds={dicomBounds}
                toggleSectionViewModeSimple={toggleSectionViewModeSimple}
                isPointLinkingMode={isPointLinkingMode}
                togglePointLinkingMode={togglePointLinkingMode}
                toggleExtractMode={toggleExtractMode}
                isExtractMode={isExtractMode}
                isSurgeryPosMode={isSurgeryPosMode}
                isSectionViewMode={isSectionViewMode}
                isCameraOrientationMode={isCameraOrientationMode}
                toggleCenterlineMode={toggleCenterlineMode}
                isCenterlineMode={isCenterlineMode}
                isStentMode={isStentMode}
                toggleStentMode={toggleStentMode}
              />

              {isDistanceMeasuringMode && (
                <DistanceMeasuringModal
                  clearMeasurements={clearMeasurements}
                  closePolygon={closePolygon}
                  setIsMeasurementModalOpen={setIsMeasurementModalOpen}
                  pathPoints={pathPoints}
                  measureAngle={measureAngle}
                  measureThickness={() => { }}
                  calculateMeshVolume={calculateMeshVolume}
                  clearVolumeMeasurement={clearVolumeMeasurement}
                  selectedMeshVolume={selectedMeshVolume}
                  isVolumeSelectionMode={isVolumeSelectionMode}
                  toggleVolumeSelectionMode={toggleVolumeSelectionMode}
                  closeDistanceMeasuringMode={toggleDistanceMeasuringMode}
                  isMobile={isMobile}
                />
              )}
              {isCameraSliderModalOpen && shortestPath.length >= 2 && (
                <CameraSliderModal
                  currentIndex={currentIndex}
                  setCurrentIndex={setCurrentIndex}
                  maxIndex={shortestPath.length - 2}
                  moveCameraToIndex={moveCameraToIndex}
                  autoMoveCamera={autoMoveCamera}
                  stopAutoMoveCamera={stopAutoMoveCamera}
                  isMobile={isMobile}
                />
              )}

              {isCenterlineMode && (
                <CenterlineModal
                  isOpen={isCenterlineMode}
                  onToggleCenterline={toggleCenterlineMeshSelectionMode}
                  onSelectPoints={selectTwoPoints}
                  onToggleCameraSliderModal={() => {
                    setIsCameraSliderModalOpen((prev) => !prev);
                    setIsCenterlineMeshSelectMode(false);
                    setIsSectionViewSliderModalOpen(false);
                  }}
                  onToggleSectionViewSliderModal={() => {
                    setIsCenterlineMeshSelectMode(false);
                    setIsCameraSliderModalOpen(false);
                    setIsSectionViewSliderModalOpen((prev) => {
                      const newState = !prev;
                      if (!newState) {
                        removeSectionViewHelper();
                      }
                      return newState;
                    });
                  }}
                  isMobile={isMobile}
                />
              )}
              {isCenterlineMeshSelectMode && (
                <CenterlineMeshSelectionModal
                  isMobile={isMobile}
                  modelRef={modelRef}
                  onMoveToStructure={handleMoveToStructure}
                  onCenterline={extractCenterline}
                  onOpacityChange={handleOpacityChange}
                  onVisibilityChange={handleVisibilityChange}
                />
              )}
              {isSectionViewSliderModalOpen && shortestPath.length >= 2 && (
                <SectionViewSliderModal
                  pathSliderValue={pathSliderValue}
                  setPathSliderValue={setPathSliderValue}
                  maxIndex={shortestPath.length - 1}
                  applySectionViewAtIndex={applySectionViewAtIndex}
                  isMobile={isMobile}
                />
              )}
              {isSectionViewSliderModalOpen && shortestPath.length >= 2 && (
                <CrossSectionModal
                  crossSectionPoints={crossSectionPoints}
                  isMobile={isMobile}
                />
              )}
              {isScalpelMode && (
                <ScalpelModal
                  isMobile={isMobile}
                  cutHeight={cutHeight}
                  setCutHeight={setCutHeight}
                  modelRef={modelRef}
                  selectedModels={selectedModels}
                  setSelectedModels={setSelectedModels}
                  setProcessing={setProcessing}
                  modifyGeometryAndAddToScene={modifyGeometryAndAddToScene}
                  closeScalpelModal={toggleScalpelMode}
                />
              )}

              {isExtractMode && (
                <ExtractModal
                  isMobile={isMobile}
                  cutHeight={extractHeight}
                  setCutHeight={setExtractHeight}
                  extractColor={extractColor}
                  setExtractColor={setExtractColor}
                  extractedName={extractedName}
                  setExtractedName={setExtractedName}
                  modelRef={modelRef}
                  selectedModels={selectedModels}
                  setSelectedModels={setSelectedModels}
                  setProcessing={setProcessing}
                  applyExtract={applyExtract}
                  closeExtractModal={toggleExtractMode}
                />
              )}


              {isStentMode && (
                <StentModal
                  upperDiameter={upperDiameter} setUpperDiameter={setUpperDiameter}
                  innerDiameter={innerDiameter} setInnerDiameter={setInnerDiameter}
                  lowerDiameter={lowerDiameter} setLowerDiameter={setLowerDiameter}
                  height={height} setHeight={setHeight}
                  transformMode={transformMode} setTransformMode={setTransformMode}
                  updateStentInScene={updateStentInScene}
                  downloadStentSTL={downloadStentSTL}
                  closeModal={toggleStentMode}
                  isMobile={isMobile}
                />
              )}
              {isDrillMode && (
                <DrillModal
                  brushSize={brushSize}
                  setBrushSize={setBrushSize}
                  drillShape={drillShape}
                  setDrillShape={setDrillShape}
                  closeDrillModal={toggleDrillMode}
                  isMobile={isMobile}
                />
              )}
              {isAnnotationModalOpen && (
                <div className="ui-outside-scene">
                  <AnnotationModal
                    isOpen={isAnnotationModalOpen}
                    onSave={handleSaveAnnotation}
                    onCancel={handleCancelAnnotation}
                    initialPosition={tempAnnotationDetails}
                  />
                </div>
              )}
              {isMeasurementModalOpen && (
                <div className="ui-outside-scene">
                  <MeasurementModal
                    isOpen={isMeasurementModalOpen}
                    onSave={handleSaveMeasurements}
                    onCancel={() => setIsMeasurementModalOpen(false)}
                  />
                </div>
              )}
              {isAddSurgeryPosModalOpen && (
                <div className="ui-outside-scene">
                  <AddSurgeryPosModal
                    isOpen={isAddSurgeryPosModalOpen}
                    onSave={createAndSaveSurgeryPos}
                    onCancel={() => setIsAddSurgeryPosModalOpen(false)}
                  />
                </div>
              )}

              {isCameraOrientationMode && (
                <CameraOrientationModal
                  resetCamera={resetCamera}
                  setHomeCameraView={setHomeCameraView}
                  goHomeCameraView={goHomeCameraView}
                  isHomeViewSet={Boolean(
                    homeOrientation && homeOrientation.position
                  )}
                  isCameraRotationLocked={isCameraRotationLocked}
                  toggleCameraRotationLock={toggleCameraRotationLock}
                  goTopView={goTopView}
                  goBottomView={goBottomView}
                  goLeftView={goLeftView}
                  goRightView={goRightView}
                  goFrontView={goFrontView}
                  goBackView={goBackView}
                  closeCameraOrientationMode={toggleCameraOrientationMode}
                  isMobile={isMobile}
                />
              )}
              {isSurgeryPosMode && (
                <SurgeryPositionModal
                  goToSurgeryPosition={goToSurgeryPosition}
                  saveSurgeryPosition={saveSurgeryPosition}
                  addSurgeryPos={() => setIsAddSurgeryPosModalOpen(true)}
                  deleteSurgeryPos={deleteSurgeryPos}
                  setSelectedSurgeryPosId={setSelectedSurgeryPosId}
                  selectedCase={thisCase}
                  selectedSurgeryPosId={selectedSurgeryPosId}
                  closeSurgeryPositionMode={toggleSurgeryPosMode}
                  isMobile={isMobile}
                />
              )}
              {isSectionViewMode && (
                <SectionViewModal
                  sectionViewMode={sectionViewMode}
                  setSectionViewMode={setSectionViewMode}
                  applySectionView={applySectionView}
                  planeOffset={planeOffset}
                  setPlaneOffset={setPlaneOffset}
                  showHelper={showHelper}
                  setShowHelper={setShowHelper}
                  setInvertClip={setInvertClip}
                  closeSectionViewModal={toggleSectionViewMode}
                  isMobile={isMobile}
                />
              )}

              {isSectionViewModeSimple && (
                <SectionViewModalSimple
                  sectionViewModeSimple={sectionViewModeSimple}
                  setSectionViewModeSimple={setSectionViewModeSimple}
                  applySectionViewSimple={applySectionViewSimple}
                  planeOffsetSimple={planeOffsetSimple}
                  setPlaneOffsetSimple={setPlaneOffsetSimple}
                  modelRefSimple={modelRef}
                  showHelperSimple={showHelperSimple}
                  setShowHelperSimple={setShowHelperSimple}
                  setInvertClipSimple={setInvertClipSimple}
                  closeSectionViewModalSimple={toggleSectionViewModeSimple}
                  isMobile={isMobile}
                />
              )}

              {processing && (
                <div className="fixed top-0 left-0 w-full h-full flex items-center justify-center bg-black bg-opacity-50 z-50">
                  <div className="text-white text-lg font-bold">
                    Processing...
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {/* Model Grouping Modal - Rendered at root level to avoid overflow issues */}
      {showGroupingModal && (
        <ModelGroupingModal
          modelInfos={stlURLs.map((stl, index) => ({
            name: stl.name,
            ...stl,
          }))}
          existingGroups={viewerModelGroups}
          userRole={thisCase?.users?.[user?.currentUser?.id]?.role || "Viewer"}
          editMode={null}
          onClose={handleCloseGroupingModal}
          onSave={handleSaveGroups}
        />
      )}
    </div>
  );
};

export default ModelViewer;
